import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { cn } from "@/web/lib/styling";

export type TooltipColor = "red" | "yellow" | "blue" | "white";

/**
 * Per-color overrides applied to the content surface and arrow. `undefined`
 * (the default) keeps the existing primary/foreground combo so existing
 * call sites stay pixel-identical. Arrow uses SVG `fill` only so a
 * background class does not paint a rectangle behind the triangle.
 */
const colorStyles: Record<TooltipColor, { content: string; arrow: string }> = {
  yellow: {
    content: "bg-withus-yellow text-withus-dark-blue",
    arrow: "fill-withus-yellow",
  },
  red: {
    content: "bg-withus-red text-withus-white",
    arrow: "fill-withus-red",
  },
  blue: {
    content: "bg-withus-blue text-withus-white",
    arrow: "fill-withus-blue",
  },
  white: {
    content: "bg-withus-white text-withus-dark-blue",
    arrow: "fill-withus-white",
  },
};

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  children,
  useTouch = false,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root> & {
  useTouch?: boolean;
}) {
  const { open: parentOpen, onOpenChange: parentOnOpenChange, ...rootProps } = props;
  const isParentControlled = "open" in props;

  const [touchOpen, setTouchOpen] = React.useState(false);

  const handleTouch = (_event: React.TouchEvent | React.MouseEvent) => {
    setTouchOpen(true);
  };

  const controlledProps = isParentControlled
    ? { open: parentOpen, onOpenChange: parentOnOpenChange }
    : useTouch
      ? { open: touchOpen, onOpenChange: setTouchOpen }
      : {};

  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...controlledProps} {...rootProps}>
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child) && useTouch && child.type === TooltipTrigger) {
            const trigger = child as React.ReactElement<React.HTMLAttributes<HTMLElement>>;
            return React.cloneElement(trigger, {
              onClick: (event: React.MouseEvent<HTMLElement>) => {
                handleTouch(event);
                trigger.props.onClick?.(event);
              },
            });
          }
          return child;
        })}
      </TooltipPrimitive.Root>
    </TooltipProvider>
  );
}

function TooltipTrigger({
  asChild = true,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" asChild={asChild} {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  collisionPadding = 8,
  children,
  hideArrow = false,
  color,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  hideArrow?: boolean;
  /**
   * Optional brand-tinted bubble. `undefined` keeps the existing primary/
   * foreground style. New call sites should prefer one of the named colors;
   * legacy ones can be left alone.
   */
  color?: TooltipColor;
}) {
  const tint = color ? colorStyles[color] : undefined;
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          "z-50 w-auto max-w-xs origin-(--radix-tooltip-content-transform-origin) animate-in bg-primary px-3 py-1.5",
          "text-xs text-pretty text-primary-foreground fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2",
          "data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          "shadow-withus-default",
          tint?.content,
          className,
        )}
        side="bottom"
        {...props}
      >
        {!hideArrow && (
          <TooltipPrimitive.Arrow
            className={cn(
              // Radix Arrow is already an SVG triangle; rotate-45 turned it into a diamond that overlapped text.
              "relative z-0 h-1.5 w-3 fill-primary",
              tint?.arrow,
            )}
          />
        )}
        <span className="relative z-10">{children}</span>
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
